import type { RemoteDefinition } from '@host/mf'

/**
 * Конфигурация приложения, собранная из окружения в одном месте.
 *
 * Пакеты `@host/*` не читают import.meta.env сами: они платформо-независимы,
 * а у desktop-сборки переменные приезжают иначе. Приложение читает окружение
 * и передаёт значения внутрь явно.
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? ''
export const SOCKET_PATH = import.meta.env.VITE_SOCKET_PATH ?? '/socket.io'

/**
 * Работает ли приложение против dev-бэкенда, а не настоящего API.
 *
 * Нужно ровно одному месту — подсказке с демо-доступом на экране входа.
 * Условие повторяет то, по которому подключается плагин в vite.config.ts:
 * dev-API включён, пока не задан адрес настоящего API и его не выключили явно.
 */
export const DEV_API_ENABLED =
  import.meta.env.DEV &&
  import.meta.env.VITE_ENABLE_DEV_API !== 'false' &&
  !import.meta.env.VITE_API_BASE_URL

/**
 * Ремоуты из переменной окружения: `имя@адрес` через запятую.
 * Нужны, когда микрофронт поднят локально и в общий манифест ещё не попал.
 */
export function parseRemotesFromEnv(raw = import.meta.env.VITE_REMOTES ?? ''): RemoteDefinition[] {
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .flatMap((entry) => {
      const separator = entry.indexOf('@')
      if (separator <= 0) return []

      const name = entry.slice(0, separator).trim()
      const url = entry.slice(separator + 1).trim()
      return name && url ? [{ name, entry: url }] : []
    })
}

export const QUERY_CLIENT_OPTIONS = {
  /**
   * 30 секунд «свежести» — компромисс для ленты: за это время данные
   * не успевают устареть настолько, чтобы это было заметно, а переходы
   * между разделами перестают дёргать сеть.
   */
  staleTime: 30_000,
  /** Реалтайм-обновления приходят сокетом, поэтому фокус окна ничего не решает. */
  refetchOnWindowFocus: false,
  retry: 1,
} as const

export const APP_NAME = 'Prismeet'
